import React, { useEffect, useState } from "react";
import KlubbVelger, { getKlubbId, getKlubbNavn } from "./KlubbVelger";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const EMPTY_LINE = {
  ean: "",
  productName: "",
  size: "",
  color: "",
  quantity: "",
  unitPrice: "",
  discountPercent: "",
  amountExclVat: "",
  amountInclVat: "",
  receiptNumber: "",
  transactionDate: "",
  teamId: "",
  departmentId: "",
  trainerId: "",
  includeInInventory: true,
};

const inputCls = "w-full border border-gray-300 rounded-md px-2 py-1.5 text-sm";

function StatusBadge({ status }) {
  const cls = status === "IMPORTERT" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700";
  const label = status === "IMPORTERT" ? "Importert" : "Venter kontroll";
  return <span className={`px-2 py-0.5 rounded text-xs font-medium ${cls}`}>{label}</span>;
}

function formatDate(iso) {
  return iso ? new Date(iso).toLocaleDateString("no-NO") : "—";
}

// Mottaker velges eksplisitt (klubb / gren / lag / trenertøy) i stedet for å tolkes fra
// fakturaen — fakturaen sier som regel bare klubbens juridiske navn (f.eks. "IL ROS EHF").
// Trenertøy er personlig eiendom for treneren og går aldri inn i klubbens/lagets lager.
function recipientValue(line) {
  if (line.trainerId) return `TRENER:${line.trainerId}`;
  if (line.teamId) return `LAG:${line.teamId}`;
  if (line.departmentId) return `GREN:${line.departmentId}`;
  return "KLUBB";
}

function parseRecipientValue(value, teams) {
  if (value.startsWith("LAG:")) {
    const teamId = value.slice(4);
    const team = teams.find((t) => t._id === teamId);
    return { teamId, departmentId: team?.departmentId?._id || "", trainerId: "" };
  }
  if (value.startsWith("GREN:")) {
    return { teamId: "", departmentId: value.slice(5), trainerId: "" };
  }
  if (value.startsWith("TRENER:")) {
    return { teamId: "", departmentId: "", trainerId: value.slice(7) };
  }
  return { teamId: "", departmentId: "", trainerId: "" };
}

function formatRecipient(clubName, departmentName, teamName, trainerName) {
  if (trainerName) return `Trenertøy: ${trainerName}`;
  if (!clubName) return teamName || (departmentName ? `${departmentName} (ikke fordelt)` : "Ufordelt");
  if (teamName) return `${clubName} - ${departmentName} - ${teamName}`;
  if (departmentName) return `${clubName} - ${departmentName} (ikke fordelt)`;
  return clubName;
}

function RecipientSelect({ value, onChange, clubName, departments, teams, trainers, dataLoading }) {
  if (dataLoading) {
    return (
      <select className={inputCls} value="KLUBB" disabled>
        <option value="KLUBB">Laster grener og lag …</option>
      </select>
    );
  }
  return (
    <select className={inputCls} value={value} onChange={onChange}>
      <option value="KLUBB">{clubName || "Klubb"}</option>
      {departments.map((d) => (
        <optgroup key={d._id} label={`${clubName || ""} - ${d.name}`.replace(/^ - /, "")}>
          <option value={`GREN:${d._id}`}>{clubName ? `${clubName} - ${d.name} (ikke fordelt)` : `${d.name} (ikke fordelt)`}</option>
          {teams
            .filter((t) => t.departmentId?._id === d._id)
            .map((t) => (
              <option key={t._id} value={`LAG:${t._id}`}>
                {clubName ? `${clubName} - ${d.name} - ${t.name}` : `${d.name} - ${t.name}`}
              </option>
            ))}
        </optgroup>
      ))}
      {trainers.length > 0 && (
        <optgroup label="Trenertøy (personlig, ikke klubbens lager)">
          {trainers.map((t) => (
            <option key={t.person._id} value={`TRENER:${t.person._id}`}>
              Trenertøy: {t.person.name}{t.teams.length ? ` (${t.teams.join(", ")})` : ""}
            </option>
          ))}
        </optgroup>
      )}
    </select>
  );
}

function InvoiceDetailModal({ invoice, loading, error, onClose, onApprove, approving, clubName }) {
  useEffect(() => {
    function onKey(e) {
      if (e.key === "Escape") onClose();
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[85vh] overflow-y-auto">
        {loading ? (
          <p className="text-gray-400 text-sm p-8">Laster …</p>
        ) : error ? (
          <p className="text-red-600 text-sm p-8">{error}</p>
        ) : invoice ? (
          <div className="p-6">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Faktura {invoice.invoiceNumber}</h2>
                <p className="text-sm text-gray-500">{invoice.customerName || "—"}</p>
              </div>
              <div className="flex items-center gap-3">
                <StatusBadge status={invoice.status} />
                <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl leading-none">×</button>
              </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm mb-6">
              <div>
                <p className="text-gray-400 text-xs uppercase">Fakturadato</p>
                <p className="text-gray-900">{formatDate(invoice.invoiceDate)}</p>
              </div>
              <div>
                <p className="text-gray-400 text-xs uppercase">Forfallsdato</p>
                <p className="text-gray-900">{formatDate(invoice.dueDate)}</p>
              </div>
              <div>
                <p className="text-gray-400 text-xs uppercase">Beløp eks. mva</p>
                <p className="text-gray-900">{invoice.totalExclVat?.toLocaleString("no-NO")} kr</p>
              </div>
              <div>
                <p className="text-gray-400 text-xs uppercase">Beløp inkl. mva</p>
                <p className="text-gray-900 font-medium">{invoice.totalInclVat?.toLocaleString("no-NO")} kr</p>
              </div>
            </div>

            {invoice.lines.some((l) => l.trainerId) && (
              <p className="text-sm text-gray-500 mb-4 bg-gray-50 rounded-lg px-3 py-2">
                Hvorav trenertøy (personlig, ikke klubbens lager):{" "}
                <span className="font-medium text-gray-900">
                  {invoice.lines.filter((l) => l.trainerId).reduce((sum, l) => sum + (l.amountInclVat || 0), 0).toLocaleString("no-NO")} kr
                </span>{" "}
                av totalt {invoice.totalInclVat?.toLocaleString("no-NO")} kr
              </p>
            )}

            <div className="overflow-x-auto -mx-1">
              <table className="w-full text-sm min-w-[900px]">
                <thead className="text-xs text-gray-500 uppercase">
                  <tr>
                    <th className="text-left px-2 py-1">EAN</th>
                    <th className="text-left px-2 py-1">Artikkelnavn</th>
                    <th className="text-left px-2 py-1">Str./Farge</th>
                    <th className="text-right px-2 py-1">Antall</th>
                    <th className="text-right px-2 py-1">Enhetspris</th>
                    <th className="text-right px-2 py-1">Rabatt %</th>
                    <th className="text-right px-2 py-1">Beløp inkl. mva</th>
                    <th className="text-left px-2 py-1">Kvittering</th>
                    <th className="text-left px-2 py-1">Mottaker</th>
                    <th className="text-center px-2 py-1">Lager</th>
                  </tr>
                </thead>
                <tbody>
                  {invoice.lines.map((line) => (
                    <tr key={line._id} className="border-t border-gray-100">
                      <td className="px-2 py-1.5 text-gray-500">{line.ean || "—"}</td>
                      <td className="px-2 py-1.5 text-gray-900">{line.productName}</td>
                      <td className="px-2 py-1.5 text-gray-500">{[line.size, line.color].filter(Boolean).join(" / ") || "—"}</td>
                      <td className="px-2 py-1.5 text-right tabular-nums">{line.quantity}</td>
                      <td className="px-2 py-1.5 text-right tabular-nums">{line.unitPrice?.toLocaleString("no-NO")}</td>
                      <td className="px-2 py-1.5 text-right tabular-nums">{line.discountPercent ? `${line.discountPercent}%` : "—"}</td>
                      <td className="px-2 py-1.5 text-right tabular-nums">{line.amountInclVat?.toLocaleString("no-NO")}</td>
                      <td className="px-2 py-1.5 text-gray-500">{line.receiptNumber || "—"}</td>
                      <td className="px-2 py-1.5 text-gray-900">{formatRecipient(clubName, line.departmentId?.name, line.teamId?.name, line.trainerId?.name)}</td>
                      <td className="px-2 py-1.5 text-center">{line.trainerId || line.includeInInventory === false ? "—" : "✓"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {invoice.status === "UBEHANDLET" && (
              <div className="flex justify-end mt-5">
                <button
                  onClick={() => onApprove(invoice._id)}
                  disabled={approving}
                  className="px-4 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-500 text-white text-sm font-semibold disabled:opacity-50"
                >
                  {approving ? "Importerer …" : "Godkjenn og importer"}
                </button>
              </div>
            )}
          </div>
        ) : null}
      </div>
    </div>
  );
}

export default function Fakturaimport() {
  const [teams, setTeams] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [trainers, setTrainers] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [header, setHeader] = useState({
    invoiceNumber: "",
    invoiceDate: "",
    customerName: "",
    totalExclVat: "",
    vatAmount: "",
    totalInclVat: "",
  });
  const [lines, setLines] = useState([{ ...EMPTY_LINE }]);
  const [bulkRecipient, setBulkRecipient] = useState("KLUBB");
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [approvingId, setApprovingId] = useState(null);
  const [parsing, setParsing] = useState(false);
  const [parseError, setParseError] = useState("");
  const [parseNotice, setParseNotice] = useState("");

  const [viewingInvoice, setViewingInvoice] = useState(null);
  const [viewingId, setViewingId] = useState(null);
  const [viewLoading, setViewLoading] = useState(false);
  const [viewError, setViewError] = useState("");

  const [masterStatus, setMasterStatus] = useState(null);
  const [masterImporting, setMasterImporting] = useState(false);
  const [masterError, setMasterError] = useState("");
  const [masterNotice, setMasterNotice] = useState("");

  // Klubbens egen vareliste (§32, forvaltes i Vareliste/Produkter-siden) — brukes her kun til å
  // slå opp produktinfo på EAN, slik at en varelinje med bare EAN + antall kan fylles ut
  // automatisk i stedet for å bli forkastet fordi artikkelnavn mangler.
  const [products, setProducts] = useState([]);

  async function loadAll() {
    try {
      const clubId = getKlubbId();
      const [teamsRes, departmentsRes, trainersRes, invoicesRes, masterRes, productsRes] = await Promise.all([
        fetch(`${API_BASE_URL}/klubbsystem/teams?clubId=${clubId}`),
        fetch(`${API_BASE_URL}/klubbsystem/departments?clubId=${clubId}`),
        fetch(`${API_BASE_URL}/klubbsystem/trainers?clubId=${clubId}`),
        fetch(`${API_BASE_URL}/klubbsystem/invoices?clubId=${clubId}`),
        fetch(`${API_BASE_URL}/klubbsystem/master-products`),
        fetch(`${API_BASE_URL}/klubbsystem/products?clubId=${clubId}`),
      ]);
      if (!teamsRes.ok || !invoicesRes.ok) throw new Error("Kunne ikke hente data fra klubbsport1");
      setTeams(await teamsRes.json());
      if (departmentsRes.ok) setDepartments(await departmentsRes.json());
      if (trainersRes.ok) setTrainers(await trainersRes.json());
      setInvoices(await invoicesRes.json());
      if (masterRes.ok) setMasterStatus(await masterRes.json());
      if (productsRes.ok) setProducts(await productsRes.json());
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  async function handleMasterListUpload(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setMasterImporting(true);
    setMasterError("");
    setMasterNotice("");
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await fetch(`${API_BASE_URL}/klubbsystem/master-products/import`, { method: "POST", body: formData });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.message || "Kunne ikke importere varelisten");
      }
      const result = await res.json();
      const warnings = [];
      if (result.sizeColumnFound === false) warnings.push("Fant ingen størrelseskolonne i filen — varene ble importert uten størrelse.");
      if (result.skuColumnFound === false) warnings.push("Fant ingen varenummer-/modellnummerkolonne i filen — varene ble importert uten varenummer.");
      setMasterNotice(`${result.created} nye, ${result.updated} oppdatert${result.skipped ? `, ${result.skipped} hoppet over` : ""}.${warnings.length ? " " + warnings.join(" ") : ""}`);
      const statusRes = await fetch(`${API_BASE_URL}/klubbsystem/master-products`);
      if (statusRes.ok) setMasterStatus(await statusRes.json());
    } catch (err) {
      setMasterError(err.message);
    } finally {
      setMasterImporting(false);
      e.target.value = "";
    }
  }

  useEffect(() => {
    loadAll();
  }, []);

  function updateHeader(field, value) {
    setHeader((h) => ({ ...h, [field]: value }));
  }

  function updateLine(index, field, value) {
    setLines((ls) => ls.map((l, i) => (i === index ? { ...l, [field]: value } : l)));
  }

  // Slår opp EAN mot klubbens egen vareliste når en linje ikke har annen info enn EAN og antall
  // — artikkelnavn er påkrevd for at linjen i det hele tatt skal bli lagret (§9), så uten dette
  // ville en rask registrering med bare strekkode blitt forkastet stille ved lagring.
  function autoFillFromCatalogByEan(index, ean) {
    const trimmed = ean.trim();
    if (!trimmed) return;
    const match = products.find((p) => p.ean && p.ean === trimmed);
    if (!match) return;

    setLines((ls) =>
      ls.map((l, i) => {
        if (i !== index || l.productName.trim()) return l;
        return {
          ...l,
          productName: match.name,
          unitPrice: l.unitPrice || (match.clubPrice ? String(match.clubPrice) : l.unitPrice),
          size: l.size || match.sizes?.[0] || l.size,
          color: l.color || match.colors?.[0] || l.color,
        };
      })
    );
  }

  function updateLineRecipient(index, value) {
    const { teamId, departmentId, trainerId } = parseRecipientValue(value, teams);
    setLines((ls) => ls.map((l, i) => (i === index ? { ...l, teamId, departmentId, trainerId } : l)));
  }

  // Sett mottaker for hele fakturaen på én gang, i stedet for å måtte endre linje for linje.
  // Individuelle linjer kan fortsatt overstyres etterpå (f.eks. trykk/gebyr, eller trenertøy).
  function applyBulkRecipient(value) {
    const { teamId, departmentId, trainerId } = parseRecipientValue(value, teams);
    setLines((ls) => ls.map((l) => ({ ...l, teamId, departmentId, trainerId })));
  }

  function addLine() {
    const { teamId, departmentId, trainerId } = parseRecipientValue(bulkRecipient, teams);
    setLines((ls) => [...ls, { ...EMPTY_LINE, teamId, departmentId, trainerId }]);
  }

  function removeLine(index) {
    setLines((ls) => (ls.length > 1 ? ls.filter((_, i) => i !== index) : ls));
  }

  async function handlePdfUpload(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setParsing(true);
    setParseError("");
    setParseNotice("");
    try {
      const formData = new FormData();
      formData.append("pdf", file);
      const res = await fetch(`${API_BASE_URL}/klubbsystem/invoices/parse`, { method: "POST", body: formData });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.message || "Kunne ikke tolke PDF-en");
      }
      const parsed = await res.json();

      setHeader({
        invoiceNumber: parsed.invoiceNumber || "",
        invoiceDate: parsed.invoiceDate || "",
        customerName: parsed.customerName || "",
        totalExclVat: parsed.totalExclVat || "",
        vatAmount: parsed.vatAmount || "",
        totalInclVat: parsed.totalInclVat || "",
      });

      if (parsed.lines?.length) {
        setLines(
          parsed.lines.map((l) => ({
            ean: l.ean || "",
            productName: l.productName || "",
            size: l.size || "",
            color: l.color || "",
            quantity: l.quantity ?? "",
            unitPrice: l.unitPrice ?? "",
            discountPercent: l.discountPercent ?? "",
            amountExclVat: l.amountExclVat ?? "",
            amountInclVat: l.amountInclVat ?? "",
            receiptNumber: l.receiptNumber || "",
            transactionDate: l.transactionDate || "",
            teamId: "",
            departmentId: "",
            trainerId: "",
            includeInInventory: l.suggestedIncludeInInventory !== false,
          }))
        );
        setParseNotice(`Fylte ut ${parsed.lines.length} varelinjer fra PDF-en. Kontroller tallene og velg mottaker per linje før du lagrer.`);
      } else {
        setParseNotice("Fant fakturahodet, men ingen varelinjer — legg dem til manuelt.");
      }
    } catch (err) {
      setParseError(err.message);
    } finally {
      setParsing(false);
      e.target.value = "";
    }
  }

  async function handleSaveDraft(e) {
    e.preventDefault();
    setSaving(true);
    setSaveError("");
    try {
      const payload = {
        clubId: getKlubbId(),
        invoiceNumber: header.invoiceNumber.trim(),
        invoiceDate: header.invoiceDate || null,
        customerName: header.customerName,
        totalExclVat: Number(header.totalExclVat) || 0,
        vatAmount: Number(header.vatAmount) || 0,
        totalInclVat: Number(header.totalInclVat) || 0,
        lines: lines
          .filter((l) => l.productName.trim())
          .map((l) => ({
            ean: l.ean,
            productName: l.productName.trim(),
            size: l.size,
            color: l.color,
            quantity: Number(l.quantity),
            unitPrice: Number(l.unitPrice) || 0,
            discountPercent: Number(l.discountPercent) || 0,
            amountExclVat: Number(l.amountExclVat) || 0,
            amountInclVat: Number(l.amountInclVat) || 0,
            receiptNumber: l.receiptNumber,
            transactionDate: l.transactionDate || null,
            teamId: l.teamId || null,
            departmentId: l.departmentId || null,
            trainerId: l.trainerId || null,
            includeInInventory: l.includeInInventory !== false,
          })),
      };
      if (!payload.invoiceNumber) throw new Error("Fakturanummer er påkrevd");
      if (payload.lines.length === 0) throw new Error("Minst én varelinje er påkrevd");

      const res = await fetch(`${API_BASE_URL}/klubbsystem/invoices`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.message || "Kunne ikke lagre faktura");
      }

      setHeader({ invoiceNumber: "", invoiceDate: "", customerName: "", totalExclVat: "", vatAmount: "", totalInclVat: "" });
      setLines([{ ...EMPTY_LINE }]);
      await loadAll();
    } catch (err) {
      setSaveError(err.message);
    } finally {
      setSaving(false);
    }
  }

  async function handleApprove(id) {
    setApprovingId(id);
    try {
      const res = await fetch(`${API_BASE_URL}/klubbsystem/invoices/${id}/approve`, { method: "POST" });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.message || "Kunne ikke importere faktura");
      }
      await loadAll();
      if (viewingId === id) await openInvoice(id);
    } catch (err) {
      setError(err.message);
    } finally {
      setApprovingId(null);
    }
  }

  async function openInvoice(id) {
    setViewingId(id);
    setViewLoading(true);
    setViewError("");
    try {
      const res = await fetch(`${API_BASE_URL}/klubbsystem/invoices/${id}`);
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.message || "Kunne ikke hente faktura");
      }
      setViewingInvoice(await res.json());
    } catch (err) {
      setViewError(err.message);
    } finally {
      setViewLoading(false);
    }
  }

  function closeInvoiceDetail() {
    setViewingId(null);
    setViewingInvoice(null);
    setViewError("");
  }

  // Robust selv når klubben ennå ikke har grener (f.eks. en nyopprettet klubb) — henter navnet
  // direkte fra klubbvelgeren i stedet for å utlede det fra det første grenelementet.
  const clubName = getKlubbNavn();

  return (
    <div className="max-w-5xl mx-auto py-10 px-4">
      <span className="inline-block px-2.5 py-1 rounded-full bg-indigo-100 text-indigo-700 text-xs font-semibold mb-3">
        Klubb
      </span>
      <h1 className="text-2xl font-semibold text-gray-900 mb-1">Fakturaimport</h1>
      <p className="text-gray-500 mb-6">
        Last opp PDF-fakturaen for å fylle ut skjemaet automatisk, eller registrer den manuelt. Fakturaen sier som
        regel bare klubbens navn (f.eks. «IL ROS EHF») — velg selv riktig gren og lag som mottaker per linje.
        Godkjenning oppretter varebevegelser i klubbens lager for det du velger.
      </p>

      <KlubbVelger onChange={loadAll} />

      <div className="border border-dashed border-indigo-300 bg-indigo-50/50 rounded-xl p-4 mb-6 flex items-center gap-3">
        <label className="px-3 py-1.5 rounded-lg bg-indigo-600 hover:bg-indigo-500 text-white text-sm font-semibold cursor-pointer">
          {parsing ? "Leser PDF …" : "Last opp PDF"}
          <input type="file" accept="application/pdf" className="hidden" onChange={handlePdfUpload} disabled={parsing} />
        </label>
        <p className="text-sm text-gray-500">
          {parseError ? <span className="text-red-600">{parseError}</span> : parseNotice || "Fyller ut fakturahode og varelinjer automatisk — kontroller alltid tallene før du lagrer."}
        </p>
      </div>

      <div className="border border-gray-200 rounded-xl p-4 mb-8">
        <div className="flex items-center gap-3 mb-3">
          <label className="px-3 py-1.5 rounded-lg border border-gray-300 hover:bg-gray-50 text-gray-700 text-sm font-semibold cursor-pointer whitespace-nowrap">
            {masterImporting ? "Importerer …" : "Last opp varekatalog"}
            <input
              type="file"
              accept=".xlsx,.xls,.csv"
              className="hidden"
              onChange={handleMasterListUpload}
              disabled={masterImporting}
            />
          </label>
          <p className="text-sm text-gray-500">
            {masterError ? (
              <span className="text-red-600">{masterError}</span>
            ) : masterNotice ? (
              masterNotice
            ) : masterStatus ? (
              <>
                {masterStatus.count} varer i Sport 1s masterkatalog
                {masterStatus.lastImportedAt ? ` — sist oppdatert ${formatDate(masterStatus.lastImportedAt)}` : ""}.
              </>
            ) : (
              "Ingen varekatalog lastet opp ennå."
            )}
          </p>
        </div>

        <div className="text-xs text-gray-500 border-t border-gray-100 pt-3">
          <p className="mb-1.5">
            Excel- eller CSV-fil fra varelageret (f.eks. Select), med én rad per vare. Kolonneoverskriftene må være blant disse (store/små bokstaver spiller ikke rolle):
          </p>
          <table className="text-xs mb-1.5">
            <tbody>
              <tr>
                <td className="pr-3 py-0.5 font-semibold text-gray-700">EAN <span className="font-normal text-gray-400">(påkrevd)</span></td>
                <td className="text-gray-500">EAN, EAN-kode, Eankode, Strekkode</td>
              </tr>
              <tr>
                <td className="pr-3 py-0.5 font-semibold text-gray-700">Produkt <span className="font-normal text-gray-400">(påkrevd)</span></td>
                <td className="text-gray-500">Produkt, Produktnavn, Vare, Varenavn, Artikkelnavn</td>
              </tr>
              <tr>
                <td className="pr-3 py-0.5 font-semibold text-gray-700">Merke <span className="font-normal text-gray-400">(valgfritt)</span></td>
                <td className="text-gray-500">Merke, Brand</td>
              </tr>
              <tr>
                <td className="pr-3 py-0.5 font-semibold text-gray-700">Veilpris <span className="font-normal text-gray-400">(valgfritt)</span></td>
                <td className="text-gray-500">Veilpris, Veilpriser, Utsalgspris, Utpris</td>
              </tr>
              <tr>
                <td className="pr-3 py-0.5 font-semibold text-gray-700">Størrelse <span className="font-normal text-gray-400">(anbefalt)</span></td>
                <td className="text-gray-500">Størrelse, Storrelse, Størrelser, Storrelser, Str, Str., Size</td>
              </tr>
              <tr>
                <td className="pr-3 py-0.5 font-semibold text-gray-700">Varenummer/modellnummer <span className="font-normal text-gray-400">(anbefalt)</span></td>
                <td className="text-gray-500">Varenummer, Vare nr, Artikkelnr, Artikkelnummer, Modellnr, Modellnummer, Modell, Produktnr, Produktnummer, SKU</td>
              </tr>
              <tr>
                <td className="pr-3 py-0.5 font-semibold text-gray-700">Farge <span className="font-normal text-gray-400">(valgfritt)</span></td>
                <td className="text-gray-500">Farge, Farger, Color, Colour</td>
              </tr>
            </tbody>
          </table>
          <p>
            Varer med EAN som allerede finnes fra før oppdateres (f.eks. ny veilpris) i stedet for å opprettes på nytt.
            Brukes til å rette artikkelnavn/merke under fakturatolking, og til å fylle inn riktig merke og veilpris når
            en ny vare fra en faktura legges automatisk i klubbens katalog. Samme produktnavn dukker ofte opp med
            mange ulike EAN — én pr. størrelse — så uten en størrelseskolonne kan ikke varelisten skille dem fra hverandre.
            Varenummer/modellnummer og farge tas med i importen når filen har egne kolonner for det.
          </p>
        </div>
      </div>

      {error && <p className="text-red-600 text-sm mb-4">{error}</p>}

      <form onSubmit={handleSaveDraft} className="border border-gray-200 rounded-xl p-5 mb-10">
        <h2 className="text-sm font-semibold text-gray-900 mb-3">Fakturahode</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
          <label className="text-xs text-gray-500">
            Fakturanr.
            <input aria-label="Fakturanr." className={inputCls} value={header.invoiceNumber} onChange={(e) => updateHeader("invoiceNumber", e.target.value)} required />
          </label>
          <label className="text-xs text-gray-500">
            Fakturadato
            <input aria-label="Fakturadato" type="date" className={inputCls} value={header.invoiceDate} onChange={(e) => updateHeader("invoiceDate", e.target.value)} />
          </label>
          <label className="text-xs text-gray-500 col-span-2">
            Kunde
            <input aria-label="Kunde" className={inputCls} value={header.customerName} onChange={(e) => updateHeader("customerName", e.target.value)} />
          </label>
          <label className="text-xs text-gray-500">
            Beløp eks. mva
            <input aria-label="Beløp eks. mva" type="number" step="0.01" className={inputCls} value={header.totalExclVat} onChange={(e) => updateHeader("totalExclVat", e.target.value)} />
          </label>
          <label className="text-xs text-gray-500">
            Mva
            <input aria-label="Mva" type="number" step="0.01" className={inputCls} value={header.vatAmount} onChange={(e) => updateHeader("vatAmount", e.target.value)} />
          </label>
          <label className="text-xs text-gray-500">
            Beløp inkl. mva
            <input aria-label="Beløp inkl. mva (hode)" type="number" step="0.01" className={inputCls} value={header.totalInclVat} onChange={(e) => updateHeader("totalInclVat", e.target.value)} />
          </label>
        </div>

        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <h2 className="text-sm font-semibold text-gray-900">Varelinjer</h2>
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-500 whitespace-nowrap">Mottaker for hele fakturaen:</span>
            <RecipientSelect
              value={bulkRecipient}
              onChange={(e) => setBulkRecipient(e.target.value)}
              clubName={clubName}
              departments={departments}
              teams={teams}
              trainers={trainers}
              dataLoading={loading}
            />
            <button
              type="button"
              onClick={() => applyBulkRecipient(bulkRecipient)}
              className="text-indigo-600 hover:text-indigo-500 text-sm font-medium whitespace-nowrap"
            >
              Bruk på alle linjer
            </button>
          </div>
        </div>
        <div className="overflow-x-auto -mx-1">
          <table className="w-full text-sm table-fixed">
            <thead className="text-xs text-gray-500 uppercase">
              <tr>
                <th className="text-left px-1 py-1 w-32">EAN</th>
                <th className="text-left px-1 py-1 w-40">Artikkelnavn</th>
                <th className="text-left px-1 py-1 w-16">Størrelse</th>
                <th className="text-left px-1 py-1 w-14">Antall</th>
                <th className="text-left px-1 py-1 w-20">Pris</th>
                <th className="text-left px-1 py-1 w-16">Rabatt %</th>
                <th className="text-left px-1 py-1 w-24">Beløp</th>
                <th className="text-left px-1 py-1 w-36">Mottaker</th>
                <th className="text-center px-1 py-1 w-14" title="Legg til i klubbens lagerbeholdning ved godkjenning">Lager</th>
                <th className="w-12"></th>
              </tr>
            </thead>
            <tbody>
              {lines.map((line, i) => (
                <tr key={i}>
                  <td className="px-1 py-1">
                    <input
                      aria-label="EAN"
                      placeholder="EAN"
                      className={`${inputCls} px-1.5`}
                      value={line.ean}
                      onChange={(e) => updateLine(i, "ean", e.target.value)}
                      onBlur={(e) => autoFillFromCatalogByEan(i, e.target.value)}
                    />
                  </td>
                  <td className="px-1 py-1"><input aria-label="Artikkelnavn" placeholder="Artikkelnavn" className={`${inputCls} px-1.5`} value={line.productName} onChange={(e) => updateLine(i, "productName", e.target.value)} required /></td>
                  <td className="px-1 py-1">
                    <input className={`${inputCls} px-1.5`} placeholder="Str." value={line.size} onChange={(e) => updateLine(i, "size", e.target.value)} />
                  </td>
                  <td className="px-1 py-1"><input aria-label="Antall" placeholder="Antall" type="number" className={`${inputCls} px-1.5`} value={line.quantity} onChange={(e) => updateLine(i, "quantity", e.target.value)} required /></td>
                  <td className="px-1 py-1"><input aria-label="Enhetspris" placeholder="Enhetspris" type="number" step="0.01" className={`${inputCls} px-1.5`} value={line.unitPrice} onChange={(e) => updateLine(i, "unitPrice", e.target.value)} /></td>
                  <td className="px-1 py-1"><input aria-label="Rabatt %" placeholder="Rabatt %" type="number" step="0.1" className={`${inputCls} px-1.5`} value={line.discountPercent} onChange={(e) => updateLine(i, "discountPercent", e.target.value)} /></td>
                  <td className="px-1 py-1"><input aria-label="Beløp inkl. mva" placeholder="Beløp" type="number" step="0.01" className={`${inputCls} px-1.5`} value={line.amountInclVat} onChange={(e) => updateLine(i, "amountInclVat", e.target.value)} /></td>
                  <td className="px-1 py-1">
                    <RecipientSelect
                      value={recipientValue(line)}
                      onChange={(e) => updateLineRecipient(i, e.target.value)}
                      clubName={clubName}
                      departments={departments}
                      teams={teams}
                      trainers={trainers}
                      dataLoading={loading}
                    />
                  </td>
                  <td className="px-1 py-1 text-center">
                    <input
                      type="checkbox"
                      title={line.trainerId ? "Trenertøy går aldri inn i klubbens lager" : "Legg til i klubbens lagerbeholdning ved godkjenning"}
                      checked={!line.trainerId && line.includeInInventory !== false}
                      disabled={!!line.trainerId}
                      onChange={(e) => updateLine(i, "includeInInventory", e.target.checked)}
                    />
                  </td>
                  <td className="px-1 py-1">
                    <button type="button" onClick={() => removeLine(i)} className="text-gray-400 hover:text-red-600 text-xs">Fjern</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="flex items-center gap-3 mt-3">
          <button type="button" onClick={addLine} className="text-indigo-600 hover:text-indigo-500 text-sm font-medium">+ Legg til linje</button>
          <div className="flex-1" />
          {saveError && <p className="text-red-600 text-sm">{saveError}</p>}
          <button
            type="submit"
            disabled={saving}
            className="px-4 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-500 text-white text-sm font-semibold disabled:opacity-50"
          >
            {saving ? "Lagrer …" : "Lagre faktura"}
          </button>
        </div>
      </form>

      <h2 className="text-sm font-semibold text-gray-900 mb-3">Fakturaer</h2>
      {loading ? (
        <p className="text-gray-400 text-sm">Laster …</p>
      ) : invoices.length === 0 ? (
        <p className="text-gray-400 text-sm">Ingen fakturaer registrert ennå.</p>
      ) : (
        <div className="border border-gray-200 rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-gray-500 text-xs uppercase tracking-wide">
                <tr>
                  <th className="text-left px-4 py-2.5">Fakturanr.</th>
                  <th className="text-left px-4 py-2.5">Dato</th>
                  <th className="text-left px-4 py-2.5">Kunde</th>
                  <th className="text-right px-4 py-2.5">Beløp inkl. mva</th>
                  <th className="text-left px-4 py-2.5">Status</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {invoices.map((inv) => (
                  <tr
                    key={inv._id}
                    onClick={() => openInvoice(inv._id)}
                    className="border-t border-gray-100 cursor-pointer hover:bg-gray-50"
                  >
                    <td className="px-4 py-2.5 font-medium text-gray-900">{inv.invoiceNumber}</td>
                    <td className="px-4 py-2.5 text-gray-500">{formatDate(inv.invoiceDate)}</td>
                    <td className="px-4 py-2.5 text-gray-500">{inv.customerName || "—"}</td>
                    <td className="px-4 py-2.5 text-right tabular-nums">{inv.totalInclVat?.toLocaleString("no-NO")} kr</td>
                    <td className="px-4 py-2.5"><StatusBadge status={inv.status} /></td>
                    <td className="px-4 py-2.5 text-right">
                      {inv.status === "UBEHANDLET" && (
                        <button
                          onClick={(e) => { e.stopPropagation(); handleApprove(inv._id); }}
                          disabled={approvingId === inv._id}
                          className="text-indigo-600 hover:text-indigo-500 text-sm font-medium disabled:opacity-50"
                        >
                          {approvingId === inv._id ? "Importerer …" : "Godkjenn og importer"}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {viewingId && (
        <InvoiceDetailModal
          invoice={viewingInvoice}
          loading={viewLoading}
          error={viewError}
          clubName={clubName}
          onClose={closeInvoiceDetail}
          onApprove={handleApprove}
          approving={approvingId === viewingId}
        />
      )}
    </div>
  );
}
